import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Package, AlertTriangle, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatFCFA } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const empty = { name: "", price: "", description: "", stock: "", image_url: "", category: "Autres" };
const CATEGORIES = ["Sucre", "Compléments", "Autres"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/products/image-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.image_url }));
      toast.success("Image téléversée");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const load = () => { api.get("/products").then((r) => setItems(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (p) => {
    setForm({ name: p.name, price: p.price, description: p.description || "", stock: p.stock || 0, low_stock_threshold: p.low_stock_threshold ?? 10, image_url: p.image_url || "", category: p.category || "Autres" });
    setEditId(p.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || form.price === "") {
      toast.error("Nom et prix requis");
      return;
    }
    const payload = {
      name: form.name,
      price: parseInt(form.price, 10),
      description: form.description,
      stock: parseInt(form.stock || 0, 10),
      low_stock_threshold: parseInt(form.low_stock_threshold || 10, 10),
      image_url: form.image_url || "",
      category: form.category || "Autres",
    };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Produit modifié" : "Produit ajouté");
      setOpen(false);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const remove = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Produit supprimé");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in-up" data-testid="products-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
            Catalogue
          </p>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Produits</h1>
        </div>
        <button
          data-testid="add-product-button"
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-semibold text-sm transition-colors duration-200"
        >
          <Plus className="h-4 w-4" /> Nouveau produit
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Produit</th>
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider">Stock</th>
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider text-right">Prix</th>
              <th className="px-6 py-3.5 font-semibold text-slate-500 text-xs uppercase tracking-wider text-right">Prix membre (-15%)</th>
              <th className="px-6 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  Aucun produit. Ajoutez « Diabliss 500g » pour commencer.
                </td>
              </tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors duration-150" data-testid={`product-row-${p.id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {p.image_url ? (
                      <img src={imgSrc(p.image_url)} alt={p.name} data-testid={`product-thumb-${p.id}`} className="h-10 w-10 rounded-md object-cover border border-slate-200" />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center text-slate-300"><Package className="h-4 w-4" /></div>
                    )}
                    <div>
                      <p className="font-medium text-slate-800">{p.name}</p>
                      <div className="flex items-center gap-2">
                        {p.category && <span data-testid={`product-category-${p.id}`} className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">{p.category}</span>}
                        {p.description && <p className="text-xs text-slate-400">{p.description}</p>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-600">
                  <span className="tabular">{p.stock}</span>
                  {p.stock <= (p.low_stock_threshold ?? 10) && (
                    <span data-testid={`low-stock-${p.id}`} className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 inline-flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Stock faible
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-right tabular font-medium text-slate-800">{formatFCFA(p.price)}</td>
                <td className="px-6 py-4 text-right tabular font-semibold text-amber-700">
                  {formatFCFA(Math.round(p.price * 0.85))}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-md hover:bg-slate-100 text-slate-500 transition-colors duration-150">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="p-2 rounded-md hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors duration-150">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nom</label>
                <input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Diabliss 500g" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Catégorie</label>
                <select data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500">
                  {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Prix (FCFA)</label>
                <input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="10000" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Stock</label>
                <input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="0" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Seuil d'alerte stock</label>
              <input data-testid="product-threshold-input" type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} placeholder="10" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
              <p className="text-xs text-slate-400 mt-1">Une alerte s'affiche quand le stock atteint ce niveau.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Image du produit</label>
              <div className="flex items-center gap-3">
                {form.image_url ? (
                  <img src={imgSrc(form.image_url)} alt="aperçu" data-testid="product-image-preview" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-300"><ImagePlus className="h-5 w-5" /></div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold transition-colors duration-200">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  {uploading ? "Envoi…" : (form.image_url ? "Changer l'image" : "Ajouter une image")}
                  <input data-testid="product-image-input" type="file" accept="image/*" onChange={uploadImage} disabled={uploading} className="hidden" />
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description</label>
              <input data-testid="product-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors duration-200">Annuler</button>
            <button data-testid="save-product-button" onClick={save} className="px-4 py-2 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white text-sm font-semibold transition-colors duration-200">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
